import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Tooltip as MuiTooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ReferenceLine,
} from "recharts";
import {
  formatCurrency,
  RECIPIENT_CHART_COLORS,
} from "../../../../constants/investmentLeague";

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle", cursor: "pointer" }}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const formatAxisCurrency = (val) => {
  if (val === 0) return "$0";
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val}`;
};

const formatTopBarLabel = (val) => {
  if (!val || val === 0) return "";
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(2)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val}`;
};

const RecipientCharts = ({
  units = [],
  selectedQuarter: globalQuarter = "All Quarters",
  unitType = "All Units",
}) => {
  const theme = useTheme();
  const t = theme.custom;

  // Independent or synced quarter selector for Chart 1
  const [chartQuarter, setChartQuarter] = useState(globalQuarter);

  React.useEffect(() => {
    setChartQuarter(globalQuarter);
  }, [globalQuarter]);

  // Chart 1 data: units filtered by unitType and sorted by chosen quarter investment descending
  const chart1Data = useMemo(() => {
    let filtered = [...units];
    if (unitType === "MAC") {
      filtered = filtered.filter((u) => u.unitType === "MAC");
    } else if (unitType === "Groups") {
      filtered = filtered.filter((u) => u.unitType === "Group");
    }

    const mapped = filtered.map((u) => {
      let investment = u.totalInvestment;
      if (chartQuarter === "Q1") investment = u.q1Investment;
      else if (chartQuarter === "Q2") investment = u.q2Investment;
      else if (chartQuarter === "Q3") investment = u.q3Investment;
      else if (chartQuarter === "Q4") investment = u.q4Investment;

      return {
        unitName: u.unitName,
        shortName: u.shortName,
        unitType: u.unitType,
        investment,
      };
    });

    // Sort descending by investment
    return mapped.sort((a, b) => b.investment - a.investment);
  }, [units, unitType, chartQuarter]);

  // Chart 2 data: 11 units with their quarterly ROI values
  const chart2Data = useMemo(() => {
    let filtered = [...units];
    if (unitType === "MAC") {
      filtered = filtered.filter((u) => u.unitType === "MAC");
    } else if (unitType === "Groups") {
      filtered = filtered.filter((u) => u.unitType === "Group");
    }

    // Sort consistently: MAC 1-8 then Groups
    return filtered
      .map((u) => ({
        unitName: u.unitName,
        shortName: u.shortName,
        unitType: u.unitType,
        q1Roi: u.q1Roi ?? 0,
        q2Roi: u.q2Roi ?? 0,
        q3Roi: u.q3Roi ?? 0,
        q4Roi: u.q4Roi ?? 0,
        rawQ1Roi: u.q1Roi,
        rawQ2Roi: u.q2Roi,
        rawQ3Roi: u.q3Roi,
        rawQ4Roi: u.q4Roi,
      }))
      .sort((a, b) => {
        if (a.unitType !== b.unitType) return a.unitType === "MAC" ? -1 : 1;
        return a.shortName.localeCompare(b.shortName);
      });
  }, [units, unitType]);

  const customBarTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <Box
          sx={{
            background: t.surface2,
            border: `1px solid ${t.border2}`,
            borderRadius: "8px",
            p: 1.5,
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
          }}
        >
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: t.text }}>
            {d.unitName}
          </Typography>
          <Typography sx={{ fontSize: "11px", color: t.text3, mb: 1 }}>
            {d.unitType} Recipient
          </Typography>
          <Typography sx={{ fontSize: "12px", color: RECIPIENT_CHART_COLORS.macBar, fontWeight: 600 }}>
            Investment: {formatCurrency(d.investment)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const customRoiTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <Box
          sx={{
            background: t.surface2,
            border: `1px solid ${t.border2}`,
            borderRadius: "8px",
            p: 1.5,
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
            minWidth: 150,
          }}
        >
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: t.text, mb: 0.5 }}>
            {d.unitName}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography sx={{ fontSize: "11px", color: RECIPIENT_CHART_COLORS.q1 }}>
              Q1 ROI: {d.rawQ1Roi != null ? `${d.rawQ1Roi > 0 ? "+" : ""}${d.rawQ1Roi.toFixed(2)}%` : "-"}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: RECIPIENT_CHART_COLORS.q2 }}>
              Q2 ROI: {d.rawQ2Roi != null ? `${d.rawQ2Roi > 0 ? "+" : ""}${d.rawQ2Roi.toFixed(2)}%` : "-"}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: RECIPIENT_CHART_COLORS.q3 }}>
              Q3 ROI: {d.rawQ3Roi != null ? `${d.rawQ3Roi > 0 ? "+" : ""}${d.rawQ3Roi.toFixed(2)}%` : "-"}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: RECIPIENT_CHART_COLORS.q4 }}>
              Q4 ROI: {d.rawQ4Roi != null ? `${d.rawQ4Roi > 0 ? "+" : ""}${d.rawQ4Roi.toFixed(2)}%` : "-"}
            </Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
        gap: 2,
        mb: 3,
      }}
    >
      {/* Chart 1: Investment Received by Unit */}
      <Box
        sx={{
          background: t.surface,
          border: `1px solid ${t.border}`,
          borderRadius: "10px",
          p: { xs: 2, md: 2.5 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "15px", fontWeight: 600, color: t.text }}>
              Investment Received by Unit ({chartQuarter})
            </Typography>
            <MuiTooltip title="Total capital investment received by each unit across the selected period.">
              <Box sx={{ color: t.text3, display: "inline-flex" }}>
                <InfoIcon />
              </Box>
            </MuiTooltip>
          </Box>

          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select
              value={chartQuarter}
              onChange={(e) => setChartQuarter(e.target.value)}
              sx={{
                borderRadius: "16px",
                fontSize: "11px",
                fontWeight: 600,
                background: t.surface2,
                "& .MuiSelect-select": {
                  py: 0.5,
                  px: 1.5,
                  minHeight: "unset !important",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: t.border2,
                },
              }}
            >
              <MenuItem value="All Quarters">All Quarters</MenuItem>
              <MenuItem value="Q1">Q1</MenuItem>
              <MenuItem value="Q2">Q2</MenuItem>
              <MenuItem value="Q3">Q3</MenuItem>
              <MenuItem value="Q4">Q4</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ width: "100%", height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chart1Data}
              margin={{ top: 20, right: 10, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={t.border} vertical={false} />
              <XAxis
                dataKey="shortName"
                tick={{ fill: t.text3, fontSize: 11 }}
                axisLine={{ stroke: t.border }}
                tickLine={false}
              />
              <YAxis
                tickFormatter={formatAxisCurrency}
                tick={{ fill: t.text3, fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                domain={[0, "auto"]}
              />
              <Tooltip content={customBarTooltip} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar
                dataKey="investment"
                radius={[4, 4, 0, 0]}
                label={{
                  position: "top",
                  fill: t.text,
                  fontSize: 10,
                  formatter: formatTopBarLabel,
                }}
              >
                {chart1Data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.unitType === "Group" ? RECIPIENT_CHART_COLORS.groupBar : RECIPIENT_CHART_COLORS.macBar}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {/* Chart 2: Quarterly ROI by Unit */}
      <Box
        sx={{
          background: t.surface,
          border: `1px solid ${t.border}`,
          borderRadius: "10px",
          p: { xs: 2, md: 2.5 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "15px", fontWeight: 600, color: t.text }}>
              Quarterly ROI by Unit
            </Typography>
            <MuiTooltip title="Return on Investment generated by each recipient unit across Q1, Q2, Q3, and Q4 based on their variance.">
              <Box sx={{ color: t.text3, display: "inline-flex" }}>
                <InfoIcon />
              </Box>
            </MuiTooltip>
          </Box>

          {/* Legend */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: RECIPIENT_CHART_COLORS.q1 }} />
              <Typography sx={{ fontSize: "11px", color: t.text2 }}>Q1</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: RECIPIENT_CHART_COLORS.q2 }} />
              <Typography sx={{ fontSize: "11px", color: t.text2 }}>Q2</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: RECIPIENT_CHART_COLORS.q3 }} />
              <Typography sx={{ fontSize: "11px", color: t.text2 }}>Q3</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: RECIPIENT_CHART_COLORS.q4 }} />
              <Typography sx={{ fontSize: "11px", color: t.text2 }}>Q4</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ width: "100%", height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chart2Data}
              margin={{ top: 10, right: 10, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={t.border} vertical={false} />
              <XAxis
                dataKey="shortName"
                tick={{ fill: t.text3, fontSize: 11 }}
                axisLine={{ stroke: t.border }}
                tickLine={false}
              />
              <YAxis
                tickFormatter={(val) => `${val}%`}
                tick={{ fill: t.text3, fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                domain={[-50, 50]}
              />
              <ReferenceLine y={0} stroke={t.border3 || "rgba(255,255,255,0.2)"} strokeWidth={1} />
              <Tooltip content={customRoiTooltip} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="q1Roi" name="Q1 ROI" fill={RECIPIENT_CHART_COLORS.q1} radius={[2, 2, 0, 0]} />
              <Bar dataKey="q2Roi" name="Q2 ROI" fill={RECIPIENT_CHART_COLORS.q2} radius={[2, 2, 0, 0]} />
              <Bar dataKey="q3Roi" name="Q3 ROI" fill={RECIPIENT_CHART_COLORS.q3} radius={[2, 2, 0, 0]} />
              <Bar dataKey="q4Roi" name="Q4 ROI" fill={RECIPIENT_CHART_COLORS.q4} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default RecipientCharts;

